import type { ActivityId, Session, SessionStatus } from "../mockData";
import { ACTIVITIES } from "../mockData";
import type { IListItem } from "./types";
import { FOOTBALL_SPORT_IDS, FOOTBALL_ACTIVITIES } from "../constants/football";

// Mirrors NetCoreInterfaces/TargetConf.cs:14 (SportId enum).
const SPORT_ID_TO_ACTIVITY: Record<number, ActivityId> = {
  1: "golfSwingFaceOn",
  2: "tennisServeDTL",
  3: "golfSwingDTL",
  4: "golfPuttingFaceOn",
  5: "golfPuttingDTL",
  6: "golfSwingFaceOn", // golfChippingFaceOn — no chipping variant in mock; fall back to swing
  7: "golfSwingDTL",    // golfChippingDTL — ditto
  8: "tennisServeFaceOn",
  9: "tennisServeFaceOn", // tennisServeFrontView
  10: "tennisForehand",   // tennisForehandFaceOn
  11: "tennisForehand",   // tennisForehandDTL
  12: "tennisForehand",   // tennisForehandFrontView
  13: "tennisBackhand",   // tennisBackhandFaceOn
  14: "tennisBackhand",   // tennisBackhandDTL
  15: "tennisBackhand",   // tennisBackhandFrontView
  16: "cricketBatting",   // cricketBattingFrontView
  17: "cricketBatting",   // cricketBattingSideView
  18: "cricketBowlingDtl",   // cricketBowlingDTL
  19: "cricketBowlingFront", // cricketBowlingFrontView
  20: "bowling",          // bowlingSideView
  21: "bowling",          // bowlingDTL
  // Movement / physio
  22: "runGait",
  23: "verticalJump",
  48: "verticalJumpFront",
  24: "squatLift",
  // Squat, the other angles (44 and 45; 42 and 43 are the Warrior II PR's)
  44: "squatFront",
  45: "squatBehind",
  // Burpee, side view: rep-based, one angle (every burpee cue is sagittal)
  75: "burpeeSide",
  // Pull-up, both angles (73 front is the primary; 74 side)
  73: "pullUpFront",
  74: "pullUpSide",
  // Lunge, a technique per camera (rep-based, body-pose only)
  71: "lungeSide",
  72: "lungeFront",
  // Push-up: a rep-based technique per angle, on the squat's pattern
  66: "pushUpSide",
  67: "pushUpFront",
  // Workplace ergonomics
  25: "wpLift",
  26: "wpPushPull",
  27: "wpOverhead",
  28: "wpRepetitive",
  29: "wpLadder",
  // Dance
  30: "danceRenegade",
  31: "danceSavage",
  32: "danceLevitating",
  33: "danceToosie",
  34: "danceOutWest",
  35: "danceWap",
  76: "danceIconicByMistake",
  // Skateboarding
  36: "skateOllie",
  37: "skateKickflip",
  38: "skateDropIn",
  // Football set-pieces
  [FOOTBALL_SPORT_IDS.penalty]: FOOTBALL_ACTIVITIES.penalty,
  [FOOTBALL_SPORT_IDS.freeKick]: FOOTBALL_ACTIVITIES.freeKick,
  [FOOTBALL_SPORT_IDS.corner]: FOOTBALL_ACTIVITIES.corner,
  62: "volleyballServeSide",
  63: "volleyballServeFront",
  64: "baseballPitchSide",
  65: "baseballPitchFront",
  69: "deadliftSide",
  70: "deadliftFront",
  // Yoga (static holds)
  42: "yogaWarrior2Front",
  43: "yogaWarrior2Side",
  46: "yogaDownwardDogSide",
  47: "yogaDownwardDogFront",
  58: "yogaCobraSide",
  // Boxing
  57: "boxingJabCrossSide",
  68: "boxingJabCrossFront",
  55: "yogaPlankSide",
  53: "yogaChairSide",
  54: "yogaChairFront",
  51: "basketballFreeThrowSide",
  52: "basketballFreeThrowFront",
  60: "yogaTriangleFront",
  61: "yogaTriangleSide",
  59: "yogaBridgeSide",
  49: "yogaTreeFront",
  50: "yogaTreeSide",
};

function inferActivityId(sport: IListItem["sport"]): ActivityId {
  if (typeof sport === "number") {
    // Trust the backend's numeric sportId; only fall back to "unknown" if it's genuinely unmapped
    // (e.g. UNK=0). Never silently call it golfSwingFaceOn — that masked every unresolved sport.
    return SPORT_ID_TO_ACTIVITY[sport] ?? "unknown";
  }
  const s = (sport ?? "").toString().toLowerCase();
  const isDtl = s.includes("dtl") || s.includes("down the line");
  // Putting must be checked before the generic dtl/face branches below, which
  // would otherwise swallow "golf putting (face-on)" into the swing variants.
  if (s.includes("putt")) return isDtl ? "golfPuttingDTL" : "golfPuttingFaceOn";
  // Cricket bowling, for the same reason putting is above: its DTL angle is spelled "DTL" too, so the
  // generic isDtl branch below swallowed "Cricket Bowling DTL" into golfSwingDTL.
  if (s.includes("bowling") && s.includes("cricket")) return s.includes("front") ? "cricketBowlingFront" : "cricketBowlingDtl";
  if (isDtl)
    return s.includes("tennis") ? "tennisServeDTL" : "golfSwingDTL";
  if (s.includes("face") && s.includes("tennis")) return "tennisServeFaceOn";
  if (s.includes("face") && s.includes("golf")) return "golfSwingFaceOn";
  if (s.includes("forehand")) return "tennisForehand";
  if (s.includes("backhand")) return "tennisBackhand";
  if (s.includes("batting")) return "cricketBatting";
  if (s.includes("bowling")) return "bowling";
  if (s.includes("run") || s.includes("gait")) return "runGait";
  // The angled variants must be tested BEFORE the family's catch-all, or the catch-all shadows
  // them: merging two techniques that each added their own rule left "jump" answering twice.
  if (s.includes("volleyball") || s.includes("serve") && s.includes("volley")) return s.includes("front") ? "volleyballServeFront" : "volleyballServeSide";
  if (s.includes("pitch") || s.includes("baseball")) return s.includes("front") ? "baseballPitchFront" : "baseballPitchSide";
  if (s.includes("deadlift")) return s.includes("front") ? "deadliftFront" : "deadliftSide";
  if (s.includes("jump")) return s.includes("front") ? "verticalJumpFront" : "verticalJump";
  // The angled variants come before any catch-all, as the jump and squat rules above do.
  if (s.includes("pull-up") || s.includes("pull up") || s.includes("pullup") || s.includes("chin-up") || s.includes("chin up"))
    return s.includes("side") ? "pullUpSide" : "pullUpFront";
  if (s.includes("lunge") && s.includes("front")) return "lungeFront";
  if (s.includes("lunge")) return "lungeSide";
  if (s.includes("squat") && s.includes("front")) return "squatFront";
  if (s.includes("squat") && s.includes("behind")) return "squatBehind";
  if (s.includes("burpee")) return "burpeeSide";
  if (s.includes("squat") || s.includes("lift")) return "squatLift";
  if (s.includes("push")) return s.includes("front") ? "pushUpFront" : "pushUpSide";
  if (s.includes("ollie")) return "skateOllie";
  if (s.includes("kickflip")) return "skateKickflip";
  if (s.includes("drop")) return "skateDropIn";
  if (s.includes("penalty")) return FOOTBALL_ACTIVITIES.penalty;
  if (s.includes("free kick") || s.includes("freekick")) return FOOTBALL_ACTIVITIES.freeKick;
  if (s.includes("corner")) return FOOTBALL_ACTIVITIES.corner;
  if (s.includes("warrior") && s.includes("side")) return "yogaWarrior2Side";
  if (s.includes("warrior")) return "yogaWarrior2Front";
  if (s.includes("downward") || s.includes("down dog")) return s.includes("front") ? "yogaDownwardDogFront" : "yogaDownwardDogSide";
  if (s.includes("cobra") || s.includes("bhujangasana")) return "yogaCobraSide";
  if (s.includes("jab") || s.includes("boxing")) return s.includes("front") ? "boxingJabCrossFront" : "boxingJabCrossSide";
  if (s.includes("plank")) return "yogaPlankSide";
  if (s.includes("chair")) return s.includes("front") ? "yogaChairFront" : "yogaChairSide";
  if (s.includes("free throw") || s.includes("freethrow")) return s.includes("front") ? "basketballFreeThrowFront" : "basketballFreeThrowSide";
  if (s.includes("triangle") || s.includes("trikonasana")) return s.includes("side") ? "yogaTriangleSide" : "yogaTriangleFront";
  if (s.includes("bridge")) return "yogaBridgeSide";
  if (s.includes("out west") || s.includes("outwest")) return "danceOutWest";
  // The front view is the primary camera for Tree pose, so an unqualified "tree" answers front.
  if (s.includes("tree")) return s.includes("side") ? "yogaTreeSide" : "yogaTreeFront";
  if (s.includes("renegade")) return "danceRenegade";
  if (s.includes("levitating")) return "danceLevitating";
  if (s.includes("savage")) return "danceSavage";
  if (s.includes("toosie")) return "danceToosie";
  if (s.includes("iconic by mistake")) return "danceIconicByMistake";
  // Word-bounded: "wap" as a bare word, so a sport whose name merely contains the letters does not match.
  if (/\bwap\b/.test(s)) return "danceWap";
  return "unknown";   // couldn't classify — don't pretend it's golf swing
}

// Reverse of SPORT_ID_TO_ACTIVITY, grouped by sport family ("Golf", "Bowling", …). Lets the
// Sessions sport chip drive a server-side sportIds query, e.g. "Bowling" -> [20, 21].
export function sportIdsForFamily(family: string): number[] {
  const ids: number[] = [];
  for (const [sportIdStr, activityId] of Object.entries(SPORT_ID_TO_ACTIVITY)) {
    const group = ACTIVITIES.find((a) => a.id === activityId)?.group;
    if (group === family) ids.push(Number(sportIdStr));
  }
  return ids;
}

function thumbFor(activity: ActivityId): string {
  if (activity.startsWith("golf")) return "🏌️";
  if (activity.startsWith("tennis")) return "🎾";
  if (activity.startsWith("cricket")) return "🏏";
  if (activity === "bowling") return "🎳";
  if (activity === "runGait") return "🏃";
  if (activity === "verticalJump") return "🤾";
  if (activity.startsWith("lunge")) return "🏋️";
  if (activity.startsWith("squat")) return "🏋️";
  if (activity.startsWith("pullUp")) return "🧗";
  if (activity.startsWith("pushUp")) return "💪";
  if (activity.startsWith("verticalJump")) return "🤾";
  if (activity === "squatLift") return "🏋️";
  if (activity.startsWith("skate")) return "🛹";
  if (activity.startsWith("yoga")) return "🧘";
  if (activity.startsWith("dance")) return "💃";
  return "🎬";
}

function statusFor(item: IListItem): SessionStatus {
  const pct = item.analysisPercentage ?? 0;
  if (pct >= 100) return "processed";
  if (pct > 0) return "processing";
  return "queued";
}

export interface AdapterContext {
  athleteName?: string; // current user's display name (Auth0 user.name)
  athleteId?: string;
  orgId?: string;
}

function decodeName(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// Build a Session-shaped object from an API IListItem so existing UI keeps working.
// Fields the API doesn't yet provide are filled with reasonable fallbacks.
export function uploadToSession(
  item: IListItem,
  ctx: AdapterContext = {}
): Session {
  const activity = inferActivityId(item.sport);
  const displayName = decodeName(item.name ?? "");
  return {
    id: String(item.uploadId ?? item.name),
    athlete: ctx.athleteName ?? "You",
    athleteId: ctx.athleteId ?? "self",
    // The athlete IN the video (subject tag; the backend already falls back to the
    // uploader's contact). Null only when enrichment failed entirely.
    subjectName: item.subjectName ?? null,
    subjectContactId: item.subjectContactId ?? null,
    orgId: ctx.orgId,
    activity,
    // No fake fallback: a missing createdAt renders as an em dash, never "now".
    capturedAt: item.createdAt ?? "",
    durationSec: 0,
    status: statusFor(item),
    thumb: thumbFor(activity),
    // The OUTCOME is the rule score (ruleScoreOverall), not the processing percentage —
    // null until a render publishes rule scores for this upload.
    score: item.score != null ? Math.round(item.score) : null,
    rescorePending: item.rescorePending === true,
    videoOrderPending: item.videoOrderPending === true,
    metrics: {
      spineAngle: 0,
      hipRotation: 0,
      shoulderRotation: 0,
      armStraightness: 0,
      pelvicTilt: 0,
      kneeFlexion: 0,
    },
    notes: item.description ?? displayName,
  };
}

export function uploadsToSessions(
  items: IListItem[] | undefined | null,
  ctx: AdapterContext = {}
): Session[] {
  return (items ?? []).map((i) => uploadToSession(i, ctx));
}
